import * as React from 'react';

import invariant from 'invariant';
import ReactDOM from 'react-dom';

import LoadingIndicator from 'components/LoadingIndicator';
/* A utility component to watch for when the user's view moves within a certain
 * distance of the bottom of the parent scroll area. This is useful for lists
 * that load more things below the fold.
 *
 * This differs from ScrollWatch in that ScrollWatch is for monitoring an
 * element in a list changing visibility, which isn't ideal for an infinitely
 * loading list. Firstly, there's no easy way to support loading a certain
 * distance from the bottom. Second, if the item with the scroll watch doesn't
 * leave the screen (say if you used the 'threshold' prop to make the last
 * element larger to avoid the first issue), then it won't trigger unless you
 * scroll up and then back down. This component avoids both those issues.
 *
 * Usage:
 * <ul>
 *   {this.renderListItems()}
 *   <li>
 *     <ScrollLoadZone threshold={100} onZoneActivated={this.loadMoreItems}>
 *       <LoadingIndicator size="small" />
 *     </ScrollLoadZone>
 *   </li>
 * </ul>
 *
 * loadMoreItems should return a Promise, which will cause ScrollLoadZone to
 * wait until it's finished before checking whether the load zone is visible,
 * and if it is, will call onLoad again. If no promise is returned, the
 * ScrollLoadZone will wait for more movement before triggering onZoneActivated
 * again.
 */
import findScrollParent from 'utils/findScrollParent';

class ScrollLoadZone extends React.Component<{
  onZoneActivated: () => Promise<any>;
  threshold: number;
  children?: React.ReactNode;
}> {
  static defaultProps = {
    threshold: 0,
  };

  componentDidMount() {
    this._mounted = true;
    this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
    // TODO: Attach a single event listener for all instances?
    if (this._scrollParent) {
      this._scrollParent.addEventListener('scroll', this.handleScroll, false);
    }
    window.addEventListener('resize', this.handleScroll, false);
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.handleScroll, false);
    }
    window.removeEventListener('resize', this.handleScroll, false);
  }

  _mounted = false;
  _scrollParent: Node | Window | null = null;
  _promise: Promise<any> | null = null;

  getVisibility = () => {
    const node = ReactDOM.findDOMNode(this) as HTMLElement;

    invariant(node instanceof HTMLElement, 'ScrollLoadZone findDOMNode returned non HTMLElement');
    const top = this.distanceToTopOfScrollParent(node);
    const height = node.offsetHeight;

    let parentHeight;
    let parentScrollTop;

    if (this._scrollParent === window) {
      parentHeight = window.innerHeight;
      parentScrollTop = window.pageYOffset;
    } else if (this._scrollParent) {
      const scrollParent = this._scrollParent as HTMLElement;
      parentHeight = scrollParent.offsetHeight;
      parentScrollTop = scrollParent.scrollTop;
    }

    if (parentScrollTop && top + height + this.props.threshold < parentScrollTop) {
      return 'above';
    } else if (
      parentScrollTop &&
      parentHeight &&
      top - this.props.threshold > parentScrollTop + parentHeight
    ) {
      return 'below';
    } else {
      return 'visible';
    }
  };

  distanceToTopOfScrollParent = (node: HTMLElement): number => {
    if (this._scrollParent !== window && !node.offsetParent) {
      throw new Error(
        'ScrollLoadZone requires a scroll parent with `position: relative` in order to calculate ' +
          'it’s visibility.'
      );
    }
    if (
      node.offsetParent === this._scrollParent ||
      !node.offsetParent ||
      !(node.offsetParent instanceof HTMLElement)
    ) {
      return node.offsetTop;
    } else {
      return node.offsetTop + this.distanceToTopOfScrollParent(node.offsetParent);
    }
  };
  handleScroll = () => {
    const visibility = this.getVisibility();
    if (this._promise != null) {
      return;
    }
    if (visibility === 'visible') {
      if (this.props.onZoneActivated) {
        const result = this.props.onZoneActivated();

        if (result && result.then && typeof result.then === 'function') {
          this._promise = result;

          result
            .then(() => {
              // This component might have been unmounted while waiting for the promise to finish
              if (this._mounted) {
                this._promise = null;
                this.handleScroll();
              }
            })
            .catch((e: Error) => {
              console.debug(e, e && e.stack);
              console.error(
                'ScrollLoadZone - request failed - error not caught before ScrollLoadZone'
              );
            });
        }
      }
    }
  };
  render() {
    const { children } = this.props;

    return children ? (
      React.Children.only(children)
    ) : (
      <LoadingIndicator align="center" size="small" />
    );
  }
}
export default ScrollLoadZone;
